import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';

import { useAuth } from './contexts/AuthContext';
import LoadingScreen from './components/common/LoadingScreen';

const SpaceLoginPage = React.lazy(() => import('./pages/SpaceLoginPage'));
const DashboardLayout = React.lazy(() => import('./components/layout/DashboardLayout'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const AgentsPage = React.lazy(() => import('./pages/AgentsPage'));
const SkillsPage = React.lazy(() => import('./pages/SkillsPage'));
const SkillAssignmentPage = React.lazy(() => import('./pages/SkillAssignmentPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const ExtensionsPage = React.lazy(() => import('./pages/ExtensionsPage'));
const DataStructuresPage = React.lazy(() => import('./pages/DataStructuresPage'));
const CampaignsPage = React.lazy(() => import('./pages/CampaignsPage'));
const TrunksPage = React.lazy(() => import('./pages/TrunksPage'));
const AmdPage = React.lazy(() => import('./pages/AmdPage'));
const DialplansPage = React.lazy(() => import('./pages/DialplansPage'));
const LoadsPage = React.lazy(() => import('./pages/LoadsPage'));
const EndsPage = React.lazy(() => import('./pages/EndsPage'));
const AgentTestPage = React.lazy(() => import('./pages/AgentTestPage'));
const WorkflowsPage = React.lazy(() => import('./pages/WorkflowsPage'));
const EngineStatusPage = React.lazy(() => import('./pages/EngineStatusPage'));
const SimulatorPage = React.lazy(() => import('./pages/SimulatorPage'));
const SupervisorAgentsPage = React.lazy(() => import('./pages/SupervisorAgentsPage'));
const SupervisorQueuesPage = React.lazy(() => import('./pages/SupervisorQueuesPage'));
const SupervisorCallResultsPage = React.lazy(() => import('./pages/SupervisorCallResultsPage'));
const SupervisorMonitoringCenterPage = React.lazy(() => import('./pages/SupervisorMonitoringCenterPage'));
const SupervisorSkillsPage = React.lazy(() => import('./pages/SupervisorSkillsPage'));
const SkillsMonitorPage = React.lazy(() => import('./pages/SkillsMonitorPage'));
const TowerPage = React.lazy(() => import('./pages/TowerPage'));
// Temporal: maquetas de las vistas de la torre que faltan por construir.
const TeamsMockupsPage = React.lazy(() => import('./pages/TeamsMockupsPage'));
const CallLogsPage = React.lazy(() => import('./pages/CallLogsPage'));
const MultimediaPage = React.lazy(() => import('./pages/MultimediaPage'));
const AgentCallsPage = React.lazy(() => import('./pages/AgentCallsPage'));
const AgentDailyReportPage = React.lazy(() => import('./pages/AgentDailyReportPage'));
const CallLogsReportPage = React.lazy(() => import('./pages/CallLogsReportPage'));
const GenericReportPage = React.lazy(() => import('./pages/GenericReportPage'));
const ApiTokensPage = React.lazy(() => import('./pages/ApiTokensPage'));
const WebhooksPage = React.lazy(() => import('./pages/WebhooksPage'));
const WallPage = React.lazy(() => import('./pages/WallPage'));
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const CasesBoardPage = React.lazy(() => import('./pages/CasesBoardPage'));
const RolesPage = React.lazy(() => import('./pages/RolesPage'));
const BlacklistsPage = React.lazy(() => import('./pages/BlacklistsPage'));
const IntegrationsPage = React.lazy(() => import('./pages/IntegrationsPage'));
const PluginAdminPage = React.lazy(() => import('./plugins/PluginAdminPage'));

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const isLoginRoute = location.pathname === '/login' || location.pathname === '/space-login';

  if (isLoading && !isLoginRoute) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" /> : <SpaceLoginPage />
            }
          />
          <Route
            path="/space-login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" /> : <SpaceLoginPage />
            }
          />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/extensions" element={<ExtensionsPage />} />
                    <Route path="/trunks" element={<TrunksPage />} />
                    <Route path="/agents" element={<AgentsPage />} />
                    <Route path="/skill-assignment" element={<SkillAssignmentPage />} />
                    <Route path="/dialplans" element={<DialplansPage />} />
                    <Route path="/skills" element={<SkillsPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/reports/agent-daily" element={<AgentDailyReportPage />} />
                    <Route path="/reports/call-logs" element={<CallLogsReportPage />} />
                    <Route path="/reports/:reportId" element={<GenericReportPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/datastructures" element={<DataStructuresPage />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    <Route path="/amd" element={<AmdPage />} />
                    <Route path="/loads" element={<LoadsPage />} />
                    <Route path="/ends" element={<EndsPage />} />
                    <Route path="/engine-status" element={<EngineStatusPage />} />
                    <Route path="/agent-test" element={<AgentTestPage />} />
                    <Route path="/workflows" element={<WorkflowsPage />} />
                    <Route path="/simulator" element={<SimulatorPage />} />
                    <Route path="/supervision/agents" element={<SupervisorAgentsPage />} />
                    <Route path="/supervision/skills" element={<SupervisorSkillsPage />} />
                    <Route path="/supervision/skills-monitor" element={<SkillsMonitorPage />} />
                    <Route path="/supervision/queues" element={<SupervisorQueuesPage />} />
                    <Route path="/supervision/call-results" element={<SupervisorCallResultsPage />} />
                    <Route path="/supervision/monitoring-center" element={<SupervisorMonitoringCenterPage />} />
                    <Route path="/supervision/call-logs" element={<CallLogsPage />} />
                    <Route path="/multimedia" element={<MultimediaPage />} />
                    <Route path="/agent-calls" element={<AgentCallsPage />} />
                    <Route path="/wall" element={<WallPage />} />
                    <Route path="/chat" element={<ChatPage />} />
                    <Route path="/digital/cases" element={<CasesBoardPage />} />
                    <Route path="/supervision/tower" element={<TowerPage />} />
                    <Route path="/teamsmockups" element={<TeamsMockupsPage />} />
                    <Route path="/api-tokens" element={<ApiTokensPage />} />
                    <Route path="/webhooks" element={<WebhooksPage />} />
                    <Route path="/roles" element={<RolesPage />} />
                    <Route path="/blacklists" element={<BlacklistsPage />} />
                    <Route path="/integrations" element={<IntegrationsPage />} />
                    <Route path="/plugins/:slug/:pageId" element={<PluginAdminPage />} />
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                    <Route path="*" element={<Navigate to="/dashboard" />} />
                  </Routes>
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </Box>
  );
}

export default App;
